

































































































































import { Vue, Component, Watch } from "@/shared/vuepack";
import Button from "@/shared/components/ui/Button.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { NamedOnboarding as OnboardingRouteName } from "@/employee/route-names";
import { useEnrollmentStore } from "@/employee/store/enrollment";
import { useUserDiscretionaryDataStore } from "@/employee/store/userDiscretionaryDataStore";
import { useUserPersonalInfoStore } from "@/employee/store/userPersonalInfo";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import { useCountryStore } from "@/shared/store/country";
import { formatMMddyyyyDateString } from "@/shared/dateString";
import { lastFour } from "@/shared/ssn";
import {
  Address,
  CitizenshipType,
  EmploymentCategory,
  FetchState,
  citizenshipTypeDisplayMap,
  employmentCategoryDisplayMap
} from "@/shared/types";
import _ from "lodash";

interface ContactInfoDisplay {
  firstName: string;
  lastName: string;
  lastFourSsn: string;
  dateOfBirth: string;
  phoneNumber: string;
  physicalAddress: {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
  };
  mailingAddress: {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
  };
}

interface DiscretionaryDataDisplay {
  isNotUsCitizen: boolean;
  citizenship: string;
  citizenshipStatus: string;
  isEmployed: boolean;
  employmentStatus: string;
  employerName: string;
  jobTitle: string;
}

const emptyContactInfo: ContactInfoDisplay = {
  firstName: "",
  lastName: "",
  lastFourSsn: "",
  dateOfBirth: "",
  phoneNumber: "",
  physicalAddress: {
    addressLine1: "",
    addressLine2: "",
    addressLine3: ""
  },
  mailingAddress: {
    addressLine1: "",
    addressLine2: "",
    addressLine3: ""
  }
};

const emptyDiscretionaryData: DiscretionaryDataDisplay = {
  isNotUsCitizen: false,
  citizenship: "",
  citizenshipStatus: "",
  isEmployed: false,
  employmentStatus: "",
  employerName: "",
  jobTitle: ""
};

@Component({
  components: {
    Button,
    GenericLoadingSpinner
  }
})
export default class ReviewPersonalDetails extends Vue {
  contactInfoStore = useContactInfoStore();
  countryStore = useCountryStore();
  enrollmentStore = useEnrollmentStore();
  userDiscretionaryDataStore = useUserDiscretionaryDataStore();
  userPersonalInfoStore = useUserPersonalInfoStore();

  contactInfo = _.cloneDeep(emptyContactInfo);
  discretionaryData = _.cloneDeep(emptyDiscretionaryData);
  ssn = "";
  revealSsn = false;
  citizenshipCountryDisplay = "";

  get isLoaded () {
    return this.contactInfoStore.contactInformationState === FetchState.Loaded &&
      this.userDiscretionaryDataStore.fetchState === FetchState.Loaded;
  }

  get isFailed () {
    return this.contactInfoStore.contactInformationState === FetchState.Failed ||
      this.userDiscretionaryDataStore.fetchState === FetchState.Failed;
  }

  get ssnLastFour () {
    return this.ssn ? lastFour(this.ssn) : "";
  }

  @Watch("contactInfoStore.contactInformation")
  onContactInfoChanged () {
    this.initializeContactInfoFromState();
  }

  @Watch("userDiscretionaryDataStore.discretionaryData")
  onDiscretionaryDataChanged () {
    this.initializeDiscretionaryDataFromState();
    this.initializeCitizenshipCountryFromState();
  }

  @Watch("countryStore.fetchState")
  onCountryFetchStateChanged () {
    this.initializeCitizenshipCountryFromState();
  }

  @Watch("userPersonalInfoStore.personalInfo")
  onPersonalInfoChanged () {
    this.initializePersonalInfoFromState();
  }

  mounted () {
    this.initializeFromState();

    this.countryStore.getCountries();
    this.contactInfoStore.getContactInformation().catch(this.handleContactInfoError);
    this.userDiscretionaryDataStore.getData();

    if (!this.ssn) {
      this.userPersonalInfoStore.getInfo();
    }
  }

  initializeFromState () {
    this.initializeContactInfoFromState();
    this.initializeDiscretionaryDataFromState();
    this.initializeCitizenshipCountryFromState();
    this.initializeSsnFromState();
  }

  initializeContactInfoFromState () {
    this.contactInfo = _.cloneDeep(emptyContactInfo);

    if (!this.contactInfoStore.contactInformation) {
      return;
    }

    const data = this.contactInfoStore.contactInformation;

    this.contactInfo = {
      firstName: data.firstName,
      lastName: data.lastName,
      lastFourSsn: data.lastFourSsn,
      dateOfBirth: formatMMddyyyyDateString(data.dateOfBirth),
      phoneNumber: data.phoneNumber,
      physicalAddress: {
        addressLine1: data.physicalAddress.addressLine1,
        addressLine2: data.physicalAddress.addressLine2,
        addressLine3: this.formatAddressLine3(data.physicalAddress)
      },
      mailingAddress: {
        addressLine1: data.mailingAddress.addressLine1,
        addressLine2: data.mailingAddress.addressLine2,
        addressLine3: this.formatAddressLine3(data.mailingAddress)
      }
    };
  }

  initializeDiscretionaryDataFromState () {
    this.discretionaryData = _.cloneDeep(emptyDiscretionaryData);

    if (!this.userDiscretionaryDataStore.discretionaryData) {
      return;
    }

    const data = this.userDiscretionaryDataStore.discretionaryData;
    const isNotUsCitizen = data.citizenship === CitizenshipType.ResidentAlien ||
      data.citizenship === CitizenshipType.NonResidentAlien;
    const isEmployed = data.employmentCategory === EmploymentCategory.Employed ||
      data.employmentCategory === EmploymentCategory.SelfEmployed;

    this.discretionaryData = {
      isNotUsCitizen,
      citizenship: isNotUsCitizen ? "Not a U.S. citizen" : "U.S. citizen",
      citizenshipStatus: citizenshipTypeDisplayMap[data.citizenship],
      isEmployed,
      employmentStatus: employmentCategoryDisplayMap[data.employmentCategory],
      employerName: data.employerName,
      jobTitle: data.jobTitle
    };
  }

  initializeCitizenshipCountryFromState () {
    this.citizenshipCountryDisplay = "";

    if (!this.userDiscretionaryDataStore.discretionaryData || !this.userDiscretionaryDataStore.discretionaryData.citizenshipCountry) {
      return;
    }

    const countryCode = this.userDiscretionaryDataStore.discretionaryData.citizenshipCountry;
    this.citizenshipCountryDisplay = this.countryStore.getCountryNameFromCode(countryCode);
  }

  initializeSsnFromState () {
    this.ssn = "";

    if (this.enrollmentStore.previouslyEnteredPii && this.enrollmentStore.previouslyEnteredPii.ssn) {
      this.ssn = this.enrollmentStore.previouslyEnteredPii.ssn;
    } else if (this.userPersonalInfoStore.personalInfo && this.userPersonalInfoStore.personalInfo.ssn) {
      this.ssn = this.userPersonalInfoStore.personalInfo.ssn;
    }
  }

  initializePersonalInfoFromState () {
    this.ssn = "";

    if (this.userPersonalInfoStore.personalInfo && this.userPersonalInfoStore.personalInfo.ssn) {
      this.ssn = this.userPersonalInfoStore.personalInfo.ssn;
    }
  }

  goToStep (stepKey: string) {
    this.$router.push({ name: OnboardingRouteName, params: { stepKey } });
  }

  toggleSsnMask () {
    this.revealSsn = !this.revealSsn;
  }

  formatAddressLine3 (address: Address) {
    return `${address.city}, ${address.state} ${address.zip}`;
  }

  handleContactInfoError () {
    this.contactInfoStore.contactInformationState = FetchState.Failed;
  }
}
